import * as THREE from 'three/webgpu'
import type { Viewer } from '../../viewer/Viewer'
import type { LinearComponentPatternCommittedRecord } from '../LinearComponentPatternTypes'
import { LinearComponentPatternInstancedGroupProjection } from './LinearComponentPatternInstancedGroupProjection'

/**
 * 线性阵列正式记录到阵列组独立 InstancedMesh 投影的适配器。
 *
 * 设计说明：
 * 1. 装配体加载阶段创建的原有 InstancedMesh 保持不变；
 * 2. 每个阵列特征组拥有自己的投影对象和 GPU 生命周期；
 * 3. 同步只增删发生变化的阵列组，不向全局 Coordinator 注入 occurrence，也不重建其它阵列组；
 * 4. 业务复制体仍是 metadata、拓扑、历史和特征树的真相，组内 InstancedMesh 只负责渲染与拾取。
 */
export class LinearComponentPatternInstancingAdapter {
  /** 阵列分组 ID 到独立实例投影的索引。 */
  private readonly projectionByGroupObjectId = new Map<
    string,
    LinearComponentPatternInstancedGroupProjection
  >()

  /** 阵列分组 ID 到当前记录结构签名的索引。 */
  private readonly signatureByGroupObjectId = new Map<string, string>()

  /** 阵列投影结构代次。 */
  private generation = 0

  /** 适配器是否已经结束生命周期。 */
  private disposed = false

  /**
   * 创建线性阵列实例化适配器。
   *
   * @param viewer 当前 Viewer。
   */
  public constructor(private readonly viewer: Viewer) {}

  /**
   * 将正式记录增量同步为各自阵列组内部的 InstancedMesh。
   *
   * @param records 当前 Manager 的完整正式记录集合。
   */
  public synchronize(records: readonly LinearComponentPatternCommittedRecord[]): void {
    this.assertAvailable()
    const retainedGroupObjectIds = new Set(records.map(record => record.groupObjectId))
    ;[...this.projectionByGroupObjectId.keys()].forEach((groupObjectId) => {
      if (!retainedGroupObjectIds.has(groupObjectId)) {
        this.releaseGroup(groupObjectId)
      }
    })

    records.forEach((record) => {
      const group = this.viewer.findSceneObjectById(record.groupObjectId)
      if (!(group instanceof THREE.Group)) {
        this.releaseGroup(record.groupObjectId)
        return
      }

      const signature = this.createRecordSignature(record)
      const currentProjection = this.projectionByGroupObjectId.get(record.groupObjectId)
      if (
        currentProjection?.isBoundTo(group)
        && this.signatureByGroupObjectId.get(record.groupObjectId) === signature
      ) {
        return
      }

      this.releaseGroup(record.groupObjectId)
      this.generation += 1
      try {
        const projection = new LinearComponentPatternInstancedGroupProjection(
          this.viewer,
          group,
          record,
          this.generation,
        )
        this.projectionByGroupObjectId.set(record.groupObjectId, projection)
        this.signatureByGroupObjectId.set(record.groupObjectId, signature)
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        console.warn('[LinearComponentPatternInstancingAdapter] 阵列组实例投影创建失败，保留普通 Mesh', {
          groupObjectId: record.groupObjectId,
          message,
        })
      }
    })
  }

  /**
   * 释放指定阵列组拥有的独立实例投影。
   *
   * @param groupObjectId 阵列分组对象 ID。
   */
  public releaseGroup(groupObjectId: string): void {
    const projection = this.projectionByGroupObjectId.get(groupObjectId)
    projection?.dispose()
    this.projectionByGroupObjectId.delete(groupObjectId)
    this.signatureByGroupObjectId.delete(groupObjectId)
  }

  /**
   * 增量更新阵列组独立实例矩阵。
   *
   * @param changedObjects 本轮发生变换的业务对象。
   * @returns 实际更新的实例槽数量。
   */
  public updateTransforms(changedObjects: readonly THREE.Object3D[]): number {
    if (this.disposed) {
      return 0
    }
    let changedSlotCount = 0
    this.projectionByGroupObjectId.forEach((projection) => {
      changedSlotCount += projection.updateTransforms(changedObjects)
    })
    return changedSlotCount
  }

  /**
   * 创建阵列记录的结构签名。
   *
   * @param record 当前阵列正式记录。
   * @returns 只描述组内 occurrence 身份与来源关系的稳定签名。
   */
  private createRecordSignature(record: LinearComponentPatternCommittedRecord): string {
    return record.partRecords
      .map(partRecord => `${partRecord.objectId}:${partRecord.sourceObjectId}`)
      .join('|')
  }

  /** 幂等释放全部阵列组实例投影。 */
  public dispose(): void {
    if (this.disposed) {
      return
    }
    ;[...this.projectionByGroupObjectId.keys()].forEach((groupObjectId) => {
      this.releaseGroup(groupObjectId)
    })
    this.disposed = true
  }

  /** 适配器释放后禁止继续发布阵列实例组。 */
  private assertAvailable(): void {
    if (this.disposed) {
      throw new Error('线性阵列实例化适配器已释放')
    }
  }
}
